import { Formik } from "formik";
import { boolean, object, string } from "yup";

import { type PensionContractType } from "@repo/logic";
import { cs } from "@repo/ui";
import { usePensionDetail, usePutTaxOptimum } from "../../../queryHooks";
import { useDrawer } from "../../Drawer/context";
import FormMultiStepDrawer from "../../Drawer/FormMultiStepDrawer";
import {
    mapFetchApiErrorToSubmitStatus,
    SubmitStatus,
    SubmitStatusStep,
} from "../../Drawer/GlobalSteps/SubmitStatusStep";
import { TaxOptimumInfo, TaxOptimumOverview } from "./TaxOptimumSteps";

export type ITaxOptimumFormValues = {
    isTaxOptimum: boolean;
};

export type ITaxOptimumDialog = {
    contractId: string;
    contractType: PensionContractType;
};

export const TaxOptimumDialog = ({ contractId, contractType }: ITaxOptimumDialog) => {
    const { setStep } = useDrawer();
    const { data: isTaxOptimum } = usePensionDetail<boolean>({
        id: contractId,
        type: contractType,
        options: {
            select: (data) => {
                return data.contract.isTaxOptimum;
            },
        },
    });

    const { mutateAsync } = usePutTaxOptimum({ contractId, contractType });
    return (
        <Formik
            initialValues={{ isTaxOptimum }}
            validationSchema={object().shape({
                isTaxOptimum: boolean().strict(),
                valuesChanged: string().parentHasChanges({
                    isTaxOptimum,
                }),
            })}
            onSubmit={async ({ isTaxOptimum }, { setStatus }) => {
                try {
                    if (isTaxOptimum === undefined) {
                        return;
                    }
                    setStatus(SubmitStatus.Submitting);
                    await mutateAsync({ isActive: isTaxOptimum });
                    const nextStatus = SubmitStatus.Success;
                    setStatus(nextStatus);
                } catch (error) {
                    setStatus(mapFetchApiErrorToSubmitStatus(error));
                }
            }}
        >
            {({ submitForm, status, validateForm, setFieldTouched, setSubmitting, isSubmitting }) => {
                return (
                    <FormMultiStepDrawer
                        title={cs.taxOptimumDialog.taxOptimum}
                        validateForm={validateForm}
                        setFieldTouched={setFieldTouched}
                        setSubmitting={setSubmitting}
                        isSubmitting={isSubmitting}
                        formStatus={status}
                    >
                        {{
                            component: <TaxOptimumInfo key="tax-optimum-info" />,
                            stepName: "tax-optimum-info",
                        }}
                        {{
                            component: <TaxOptimumOverview key="tax-optimum-overview" />,
                            stepName: "tax-optimum-overview",
                            stepConfig: {
                                customFunction: () => {
                                    void submitForm();
                                    setStep(2);
                                },
                            },
                        }}
                        {{
                            component: <SubmitStatusStep status={status} key="tax-optimum-after-submit" />,
                            stepName: "tax-optimum-after-submit",
                            stepConfig: {
                                //work around default custom form validation function, need to return true, otherwise drawer won't close.
                                customFunction: () => true,
                            },
                        }}
                    </FormMultiStepDrawer>
                );
            }}
        </Formik>
    );
};
